import React, { useState } from "react";
import {
  BaseQueryFn,
  TypedUseQueryHookResult,
  skipToken,
} from "@reduxjs/toolkit/query/react";
import { GenericSort, Pagination } from "@sscs/types";
import {
  EmptyState,
  Page,
  hasActiveFilters,
  useFilterSortPagination,
} from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import { ORGANIZATION_CATALOG_ID, PERMISSION } from "Constants";
import { useAllowedTo } from "Hooks";
import ExternalSoftwareCard from "../../components/ExternalSoftwareCard/ExternalSoftwareCard";
import ImportPackage from "../../components/ImportPackage/ImportPackage";
import SoftwareCard from "../../components/SoftwareCard/SoftwareCard";
import useCatalogParams from "../../hooks/useCatalogParams";
import useCatalogs from "../../hooks/useCatalogs";
import {
  useGetExternalCatalogItemsQuery,
  useGetSoftwareCatalogItemsQuery,
} from "../../services/softwareCatalog.api";
import {
  ExternalCatalogSortKeys,
  ExternalPackageSummary,
  SoftwareCatalogFilter,
  SoftwareCatalogSortKeys,
} from "../../types";
import FilterAndSort from "./FilterAndSort";
import styles from "./platform.module.scss";

const INITIAL_FILTER: SoftwareCatalogFilter = {
  name: null,
  category: null,
  in_catalog: null,
  license: null,
  publisher: null,
};

const INITIAL_PAGINATION: Pagination = { currentPage: 1, itemsPerPage: 20 };

const ORGANIZATION_SORT_OPTIONS: ReadonlyArray<{
  property: SoftwareCatalogSortKeys;
  label: string;
}> = [
  { property: "popularity", label: "Popularity" },
  { property: "download_count", label: "Downloads" },
  { property: "release_date", label: "Release date" },
];

const EXTERNAL_SORT_OPTIONS: ReadonlyArray<{
  property: ExternalCatalogSortKeys;
  label: string;
}> = [
  { property: "popularity", label: "Popularity" },
  { property: "release_date", label: "Release date" },
];

type ItemsQuery<Item> = Pick<
  TypedUseQueryHookResult<
    { items: Item[]; total_packages_count: number },
    unknown,
    BaseQueryFn
  >,
  "data" | "isLoading" | "isFetching" | "isError" | "error"
>;

type PlatformBodyProps<SortKey extends string, Item> = {
  id: string;
  sortOptions: ReadonlyArray<{ property: SortKey; label: string }>;
  filter: SoftwareCatalogFilter;
  sort: GenericSort<SortKey>;
  pagination: Pagination;
  onFilterChange: (filter: SoftwareCatalogFilter) => void;
  onSortChange: (sort: GenericSort<SortKey>) => void;
  onPaginationChange: (pagination: Pagination) => void;
  query: ItemsQuery<Item>;
  renderItem: (item: Item) => React.ReactNode;
};

const PlatformBody = <SortKey extends string, Item>({
  id,
  sortOptions,
  filter,
  sort,
  pagination,
  onFilterChange,
  onSortChange,
  onPaginationChange,
  query,
  renderItem,
}: PlatformBodyProps<SortKey, Item>): React.JSX.Element => {
  const { groupId } = useCatalogParams();
  const canCurateCatalog = useAllowedTo({
    permissions: [PERMISSION.CREATE_PACKAGE],
    groupId,
  });
  const itemCount = query.data?.total_packages_count ?? 0;
  const hasResults = (query.data?.items.length ?? 0) > 0;

  return (
    <>
      <Page.Filters
        found={{
          id: `${id}-results`,
          count: itemCount,
          initialFilters: INITIAL_FILTER,
          activeFilters: filter,
          onClear: () => onFilterChange(INITIAL_FILTER),
        }}
      >
        <FilterAndSort
          filter={filter}
          sort={sort}
          sortOptions={sortOptions}
          onFilterChange={onFilterChange}
          onSortChange={onSortChange}
        />
      </Page.Filters>
      <Page.Body
        status={getQueryStatus(query)}
        fetchingClassName={styles.fetching}
      >
        {query.data && hasResults ? (
          <div id={`${id}-items`} className={styles.results}>
            {query.data.items.map((item) => renderItem(item))}
          </div>
        ) : (
          <EmptyState
            subject="software"
            hint={
              canCurateCatalog
                ? "Create a project in Projects, add a package, upload and approve a version, then publish. Or import from an external catalog in the sidebar."
                : "Your administrators add software here once it has been reviewed and approved."
            }
            filters={{
              active: hasActiveFilters(INITIAL_FILTER, filter),
              onReset: () => onFilterChange(INITIAL_FILTER),
            }}
          />
        )}
        {hasResults && (
          <Page.Pagination
            id={`${id}-pagination`}
            itemCount={itemCount}
            pagination={pagination}
            onChange={onPaginationChange}
            initialItemCount={INITIAL_PAGINATION.itemsPerPage}
          />
        )}
      </Page.Body>
    </>
  );
};

const OrganizationPlatform = (): React.JSX.Element => {
  const { groupId, platform } = useCatalogParams();
  const { selectedCatalog } = useCatalogs();
  const architecture = selectedCatalog?.path[1]?.name ?? null;
  const extension = selectedCatalog?.path[2]?.name ?? null;
  const {
    filter,
    sort,
    pagination,
    handleFilterChange,
    handleSortChange,
    handlePaginationChange,
  } = useFilterSortPagination<
    SoftwareCatalogFilter,
    GenericSort<SoftwareCatalogSortKeys>
  >(
    INITIAL_FILTER,
    { reverse: true, property: "popularity" },
    INITIAL_PAGINATION,
  );
  const query = useGetSoftwareCatalogItemsQuery(
    groupId && platform && selectedCatalog
      ? {
          groupId,
          platform,
          architecture,
          extension,
          filter,
          sort,
          pagination,
        }
      : skipToken,
  );

  return (
    <PlatformBody
      id="software-catalog"
      sortOptions={ORGANIZATION_SORT_OPTIONS}
      filter={filter}
      sort={sort}
      pagination={pagination}
      onFilterChange={handleFilterChange}
      onSortChange={handleSortChange}
      onPaginationChange={handlePaginationChange}
      query={query}
      renderItem={(software) => (
        <SoftwareCard
          key={software.project_id + software.package_id}
          software={software}
        />
      )}
    />
  );
};

const ExternalPlatform = (): React.JSX.Element => {
  const { groupId, catalogId, platform } = useCatalogParams();
  const { catalogs, selectedCatalog } = useCatalogs();
  const architecture = selectedCatalog?.path[1]?.name ?? null;
  const extension = selectedCatalog?.path[2]?.name ?? null;
  const [selectedExternalPackage, setSelectedExternalPackage] =
    useState<ExternalPackageSummary | null>(null);
  const catalogName =
    (catalogId ? catalogs?.[catalogId]?.title : undefined) ?? catalogId;
  const {
    filter,
    sort,
    pagination,
    handleFilterChange,
    handleSortChange,
    handlePaginationChange,
  } = useFilterSortPagination<
    SoftwareCatalogFilter,
    GenericSort<ExternalCatalogSortKeys>
  >(
    INITIAL_FILTER,
    { reverse: true, property: "popularity" },
    INITIAL_PAGINATION,
  );
  const query = useGetExternalCatalogItemsQuery(
    groupId && catalogId && platform && selectedCatalog
      ? {
          catalog: catalogId,
          groupId,
          platform,
          architecture,
          extension,
          filter,
          sort,
          pagination,
        }
      : skipToken,
  );

  return (
    <>
      <PlatformBody
        id="external-catalog"
        sortOptions={EXTERNAL_SORT_OPTIONS}
        filter={filter}
        sort={sort}
        pagination={pagination}
        onFilterChange={handleFilterChange}
        onSortChange={handleSortChange}
        onPaginationChange={handlePaginationChange}
        query={query}
        renderItem={(software) => (
          <ExternalSoftwareCard
            key={software.id}
            software={software}
            onImport={(item) => setSelectedExternalPackage(item)}
          />
        )}
      />
      {groupId && catalogId && selectedExternalPackage && (
        <ImportPackage
          catalogName={catalogName ?? catalogId}
          externalPackage={selectedExternalPackage}
          onClose={() => setSelectedExternalPackage(null)}
        />
      )}
    </>
  );
};

const Platform = (): React.JSX.Element => {
  const { catalogId, platform, architecture, extension } = useCatalogParams();
  const key = JSON.stringify([catalogId, platform, architecture, extension]);

  return catalogId === ORGANIZATION_CATALOG_ID ? (
    <OrganizationPlatform key={key} />
  ) : (
    <ExternalPlatform key={key} />
  );
};

export default Platform;
